import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PluralForm } from '@nimbus-library/features/localization/models/enums/pluralform.enum';
import { IInputAssignProfile } from '@nimbus-library/features/shared/components/material/input-assign-profile/domain/i-input-assign-profile';
import { orderBy, NimbusGridSort } from '@nimbus-library/features/shared/components/nimbus-grid/domain/nimbus-grid-sort';
import { NimbusGridComponent } from '@nimbus-library/features/shared/components/nimbus-grid/nimbus-grid/nimbus-grid.component';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { WindowEventsService } from '@nimbus-library/features/shared/services/window-events.service';
import { BulkMapArrayValue, BulkMapValue, BulkValue } from '@nimbus-global/features/bulk-edit/models/bulk-value';
import { IResponsibilityProfile } from '@nimbus-global/features/manage/model/responsibility-profile.model';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { NimbusGridDataView } from '../../nimbus-grid/domain/nimbus-grid-data-view';
import { IInputComponent } from '../i-input-component';
import { NimbusGridService } from '../../nimbus-grid/services/nimbus-grid.service';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-input-assign-profile',
  templateUrl: './input-assign-profile.component.html',
  styleUrls: ['./input-assign-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputAssignProfileComponent),
    },
  ],
})
export class InputAssignProfileComponent extends IInputComponent<IInputAssignProfile[]> implements AfterViewInit, OnChanges {
  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-native
  public search: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  public edit: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  public closeOverlay: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  public updateBulk: EventEmitter<IInputAssignProfile> = new EventEmitter<IInputAssignProfile>();

  @ViewChild(NimbusGridComponent, { static: true })
  public gridComponent: NimbusGridComponent;

  @Input()
  public templateReference: TemplateRef<any>;
  @Input()
  public hasUpdatePermission: boolean = true;
  @Input()
  public isBulk: boolean;
  @Input()
  public bulkValues: BulkMapArrayValue<IResponsibilityProfile>;
  @Input()
  public warningMessage: string;
  @Input()
  public container: ElementRef<HTMLElement> | HTMLElement;
  @Input()
  public isBulkReadonly: (bulkItem: any) => boolean;
  @Input()
  public availableHeight: number | null = null;

  private viewInitialized: boolean = false;
  private readonly DEBOUNCE_TIME: number = 350;
  private readonly MAX_PAGE_SIZE: number = 6;

  public entities$: Observable<NimbusGridDataView>;
  public pageSize$: BehaviorSubject<number>;
  public loading$: Observable<boolean>;
  public sort$: BehaviorSubject<NimbusGridSort[]>;
  public skip$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public valueTrigger$: ReplaySubject<void> = new ReplaySubject(1);
  public activeSelected: boolean = false;
  public defaultSelected: boolean = false;
  public visibleOptions: IInputAssignProfile[] = [];
  public allOptions: IInputAssignProfile[] = null;
  public optionsToAdd: IInputAssignProfile[] = [];
  public selectedOptions: IInputAssignProfile[] = [];
  public PluralForm = PluralForm;
  public ICONS = ICONS;

  @Input()
  public get value(): IInputAssignProfile[] {
    return this.innerValue;
  }
  public set value(value: IInputAssignProfile[]) {
    if (!isEqual(value, this.innerValue)) {
      this.innerValue = value;
      this.propagateChange(value);
      this.setSelectionByValue(value);
    }
  }

  @Input()
  public set options(options: IInputAssignProfile[]) {
    this.allOptions = options;

    if (options && options.length) {
      this.visibleOptions = options.filter((item) => item.isVisible);
      this.setSelectionByValue(this.value);
    } else {
      this.optionsToAdd = [];
      this.selectedOptions = [];
    }
  }

  public get skip(): number {
    return this.skip$.value;
  }

  public set skip(value: number) {
    this.skip$.next(value);
  }

  public constructor(
    private readonly changeDetectionRef: ChangeDetectorRef,
    private readonly resizeEventService: WindowEventsService,
    private readonly nimbusGridService: NimbusGridService,
    private readonly injector: Injector,
    private readonly translateService: TranslateService,
  ) {
    super(changeDetectionRef, injector, translateService);

    this.sort$ = new BehaviorSubject<NimbusGridSort[]>([{ field: 'name', dir: 'asc' }]);
    this.skip$ = new BehaviorSubject<number>(0);
    this.pageSize$ = new BehaviorSubject<number>(this.MAX_PAGE_SIZE);
  }

  public ngAfterViewInit(): void {
    this.entities$ = this.initializeNimbusGridDataView();

    this.resizeEventService
      .getResize$()
      .pipe(debounceTime(this.DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe(() => {
        this.refreshPageSize();
      });

    this.viewInitialized = true;
    setTimeout(() => this.refreshPageSize());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.availableHeight && this.viewInitialized) {
      this.refreshPageSize();
    }
  }

  public setSelectionByValue(value: IInputAssignProfile[]): void {
    if (value) {
      this.optionsToAdd = [];
      this.visibleOptions?.forEach((item) => {
        const itemResult = value.find((valueItem) => valueItem.id === item.id);
        if (!itemResult) {
          this.optionsToAdd.push({ ...item });
        }
      });
    } else {
      this.optionsToAdd = this.visibleOptions;
    }
    this.selectedOptions = this.value || [];
    if (!(this.changeDetectionRef as ViewRef).destroyed) {
      this.changeDetectionRef.markForCheck();
      this.valueTrigger$.next();
    }
  }

  public writeValue(value: IInputAssignProfile[]): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.setSelectionByValue(value);
    }
    this.changeDetectionRef.markForCheck();
  }

  public insertParameter(item: IInputAssignProfile): void {
    const tempValue: IInputAssignProfile[] = JSON.parse(JSON.stringify(this.value || []));
    tempValue.unshift(item);

    this.value = tempValue;
    this.emitBulkUpdate(item);
    this.changeDetectionRef.markForCheck();
  }

  public onRemoveItemClick(item: IInputAssignProfile): void {
    const tempValue: IInputAssignProfile[] = JSON.parse(JSON.stringify(this.value)).filter((tv) => tv.id !== item.id);
    if (item.active) {
      tempValue.forEach((x) => (x.active = x.default));
    }

    this.value = tempValue;

    if (this.isBulk) {
      this.bulkValues.value = this.bulkValues.value.filter((bulkItem) => bulkItem.dataField.id !== item.id);
    }
    this.emitBulkUpdate();

    this.changeDetectionRef.markForCheck();
  }

  public onApplyItem(item: IInputAssignProfile): void {
    this.emitBulkUpdate(item);
  }

  public onEditClick(item: IInputAssignProfile): void {
    this.edit.emit(item.id);
  }

  public initializeNimbusGridDataView(): Observable<NimbusGridDataView> {
    return combineLatest([this.valueTrigger$, this.sort$, this.skip$, this.pageSize$]).pipe(
      map(([, sort, skip, pageSize]) => {
        let entityList = orderBy(this.value || [], sort);
        const alignedSkip = this.nimbusGridService.alignSkipToPage(skip, pageSize, entityList.length);
        if (alignedSkip !== skip) {
          this.skip = alignedSkip;
        }
        entityList = entityList.slice(alignedSkip, alignedSkip + pageSize);
        return {
          total: this.value.length,
          data: entityList,
        } as NimbusGridDataView;
      }),
    );
  }

  public sortingChange(event: NimbusGridSort): void {
    this.sort$.next([event]);
  }

  public pagingChange(skip: number): void {
    this.skip = skip;
  }

  public onSearch(val: string): void {
    this.search.emit(val);
  }

  public onClose(): void {
    this.closeOverlay.emit();
  }

  public setDefault(id: string): void {
    this.defaultSelected = true;
    const tempValue: IInputAssignProfile[] = JSON.parse(JSON.stringify(this.value));
    tempValue.map((x) => (x.default = x.id === id));
    this.value = tempValue;

    this.updateBulkValues('default', id);
    this.changeDetectionRef.markForCheck();
  }

  public onClickSetDefault(dataItem: IInputAssignProfile): void {
    if (this.isBulk && dataItem.default && !this.defaultSelected && this.getBulkItem(dataItem.id)?.identicalItems) {
      this.setDefault(dataItem.id);
    }
  }

  public setActive(id: string): void {
    this.activeSelected = true;
    const tempValue: IInputAssignProfile[] = JSON.parse(JSON.stringify(this.value));
    tempValue.map((x) => (x.active = x.id === id));
    this.value = tempValue;

    this.updateBulkValues('active', id);
    this.changeDetectionRef.markForCheck();
  }

  public onClickSetActive(dataItem: IInputAssignProfile): void {
    if (this.isBulk && dataItem.active && !this.activeSelected && this.getBulkItem(dataItem.id)?.identicalItems) {
      this.setActive(dataItem.id);
    }
  }

  public getBulkValue(id: string, fieldName: string): BulkValue<any> {
    const bulkItem = this.getBulkItem(id);
    if (!bulkItem) {
      return;
    }
    return bulkItem.mapValues[fieldName];
  }

  public isNpdItem(id: string): boolean {
    return this.allOptions && !this.allOptions.find((option) => option.id === id);
  }

  public isBulkReadonlyLocal(id: string): boolean {
    const bulkItem = this.getBulkItem(id);
    if (!bulkItem) {
      return false;
    }
    const dataFieldId = bulkItem.dataField.id;
    return this.isNpdItem(dataFieldId);
  }

  public getBulkItem(id: string): BulkMapValue<IResponsibilityProfile> {
    if (!this.isBulk || !this.bulkValues) {
      return null;
    }
    return this.bulkValues.value.find((bulkItem) => bulkItem.dataField.id === id);
  }

  public getApplyTooltip(id: string): TranslationKeys | string {
    const dataItem = this.getBulkItem(id);
    if (this.isBulkReadonlyLocal(id)) {
      return '';
    }
    if (dataItem.identicalItems) {
      return TranslationKeys.AdminUserListProfilesApplyDisabled;
    }
    return TranslationKeys.AdminUserListProfilesApplyToSelected;
  }

  private updateBulkValues(fieldName: string, id: string): void {
    if (!this.isBulk) {
      return;
    }
    this.bulkValues?.value?.forEach((bulkItem) => {
      const mapBulkValue = bulkItem.mapValues[fieldName];
      mapBulkValue.identical = true;
      mapBulkValue.dirty = true;
      mapBulkValue.value = bulkItem.dataField.id === id;
      bulkItem.identical = bulkItem.identicalItems && bulkItem.isValuesIdentical;
    });
  }

  private refreshPageSize(): void {
    const container: HTMLElement = this.resolveContainer() || this.gridComponent.wrapper.nativeElement;
    const gridOffset = 123;
    const gridRowHeight = 37;

    const containerHeight = this.availableHeight ?? container.clientHeight;
    const pageSize = Math.min(this.nimbusGridService.calculatePageSize(containerHeight, gridOffset, gridRowHeight), this.MAX_PAGE_SIZE);

    this.pageSize$.next(pageSize);
  }

  private resolveContainer(): HTMLElement | null {
    if (!this.container) {
      return null;
    }

    return this.container instanceof ElementRef ? this.container.nativeElement : this.container;
  }

  private emitBulkUpdate(item: IInputAssignProfile | null = null): void {
    if (this.isBulk) {
      this.updateBulk.emit(item);
    }
  }
}
